import { Button, Group, Modal, Stack, Text } from '@mantine/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { useDeleteCrossCameraPipeline } from '@/queries/crossCameraQueries'
import { showToast } from '@/theme/notifications'
import { CrossCameraPipeline } from '@/types/crossCamera'

type CrossCameraPipelineDeleteModalProps = {
  pipeline: CrossCameraPipeline | null
  onClose: () => void
}

export const CrossCameraPipelineDeleteModal = ({
  pipeline,
  onClose
}: CrossCameraPipelineDeleteModalProps) => {
  const intl = useIntl()
  const { mutateAsync: deletePipeline, isPending } =
    useDeleteCrossCameraPipeline()

  const handleDeleteClick = async () => {
    if (!pipeline) return

    try {
      await deletePipeline(pipeline.id)
      showToast(
        intl.formatMessage({ id: 'crossCamera.pipelines.delete.success' }),
        'success'
      )
      onClose()
    } catch (error) {
      showToast(
        extractErrorMessage(
          error,
          intl.formatMessage({ id: 'crossCamera.pipelines.delete.error' })
        ),
        'error'
      )
    }
  }

  return (
    <Modal
      opened={pipeline !== null}
      title={<FormattedMessage id="crossCamera.pipelines.delete.title" />}
      onClose={onClose}
    >
      <Stack gap="md">
        <Text size="sm">
          <FormattedMessage
            id="crossCamera.pipelines.delete.body"
            values={{ name: <span>{pipeline?.name}</span> }}
          />
        </Text>

        <Group justify="end">
          <Button variant="subtle" onClick={onClose}>
            <FormattedMessage id="cancel" />
          </Button>
          <Button
            color="red"
            loading={isPending}
            onClick={() => void handleDeleteClick()}
          >
            <FormattedMessage id="delete" />
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
